import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxColor, OnyxIconName, OnyxOptional } from '@onyx/angular';
import {
  MapHelper,
  OnyxAddressComponent,
  OnyxDatePipe,
  OnyxIconComponent,
  OnyxInformationHeadingComponent,
  OnyxInformationListComponent,
  OnyxInformationRowComponent,
  OnyxLinkComponent,
  OnyxModalComponent,
  OnyxNamePipe,
} from '@onyx/angular';
import { DateTime } from 'luxon';
import { catchError, EMPTY, startWith, Subject, switchMap, tap } from 'rxjs';
import { UnitCellComponent } from '../../../../../common/components/cells/unit-cell/unit-cell.component';
import { SCHEDULE_COMPLIANCE_DELAY_RISK_THRESHOLD } from '../../../../../common/constants/common/schedule-compliance-delay-risk-threshold';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import type { ActivityPoint } from '../../../../../common/interfaces/common/activity';
import { FleetIdentifierPipe } from '../../../../../common/pipes/fleet-identifier.pipe';
import type { Delegation } from '../../../../delegations/common/interfaces/delegation';
import { DelegationsService } from '../../../../delegations/common/services/delegations.service';
import { DriverHelper } from '../../../../drivers/common/helpers/driver.helper';
import { FleetCategory } from '../../../../fleet/common/enums/fleet-category';
import { FleetHelper } from '../../../../fleet/common/helpers/fleet.helper';
import { EmployeeHelper } from '../../../../management-panel/employees/common/helpers/employee.helper';
import type { SimplifiedEmployee } from '../../../../management-panel/employees/common/interfaces/employee';
import { OrderProposalStatus } from '../../enums/order-proposal-status';
import { OrderProposalType } from '../../enums/order-proposal-type';
import { OrderRejectReason } from '../../enums/order-reject-reason';
import { OrderHelper } from '../../helpers/order.helper';
import type { Order, OrderProposal } from '../../interfaces/order';
import { OrdersService } from '../../services/orders.service';
import { ModuleLinkComponent } from '../../../../../common/components/module-link/module-link.component';

interface ProposalHistory {
  title: string;
  icon: OnyxIconName;
  color: OnyxColor;
  date: string;
  reason: string | null;
  author: SimplifiedEmployee | null;
  authorLabel: string | null;
}

export interface ActivityModalData {
  order?: Order | null;
  delegation?: Delegation | null;
}

@Component({
  selector: 'app-activity-modal',
  imports: [
    ModuleLinkComponent,
    OnyxModalComponent,
    OnyxInformationHeadingComponent,
    OnyxInformationListComponent,
    TranslatePipe,
    OnyxIconComponent,
    OnyxInformationRowComponent,
    OnyxLinkComponent,
    OnyxDatePipe,
    FleetIdentifierPipe,
    OnyxNamePipe,
    NgTemplateOutlet,
    UnitCellComponent,
    OnyxAddressComponent,
  ],
  templateUrl: './activity-modal.component.html',
  styleUrl: './activity-modal.component.scss',
})
export class ActivityModalComponent {
  protected readonly data = inject<ActivityModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject<DialogRef<void>>(DialogRef);
  protected readonly fleetHelper = inject(FleetHelper);
  protected readonly driverHelper = inject(DriverHelper);
  protected readonly employeeHelper = inject(EmployeeHelper);
  protected readonly orderHelper = inject(OrderHelper);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ordersService = inject(OrdersService);
  private readonly delegationsService = inject(DelegationsService);

  protected readonly I18N = 'orders.orderModal.activityModal';

  protected readonly FleetCategory = FleetCategory;
  protected readonly DictionaryCode = DictionaryCode;
  protected readonly MapHelper = MapHelper;

  protected activity$ = new Subject<void>();
  protected order = signal<Order | null>(null);
  protected loading = signal(false);
  protected error = signal(false);

  protected assignmentState = computed(() => {
    const order = this.order();
    if (!order?.timestamps.toDoAt) return null;

    const { assignedData, outsourcingData } = order;
    const acceptedProposal = this.acceptedProposal();

    if (outsourcingData) {
      return {
        title: 'labels.outsourcing',
        icon: 'cursor-filled',
        color: 'sea-green',
      };
    }

    if (assignedData && acceptedProposal) {
      if (acceptedProposal.type === OrderProposalType.ENGINE) {
        return {
          title: `${this.I18N}.engineProposalAcceptance`,
          icon: 'check-circle-filled',
          color: 'green',
        };
      }

      return {
        title: `${this.I18N}.manualAssignment`,
        icon: 'manual-filled',
        color: 'violet',
      };
    }

    return null;
  });

  protected points = computed(() => {
    const getColor = (
      completedAt: OnyxOptional<string>,
      deadline: OnyxOptional<string>,
    ): OnyxColor => {
      if (!deadline || !completedAt) return 'black';

      const diff = DateTime.fromISO(completedAt).diff(
        DateTime.fromISO(deadline),
      );

      if (diff.toMillis() < 0) return 'green';
      if (diff < SCHEDULE_COMPLIANCE_DELAY_RISK_THRESHOLD) return 'orange';
      return 'red';
    };

    return this._points()
      .map((point) => {
        const { departureDeadlineTime, arrivalDeadlineTime } =
          point.point.estimations ?? {};

        return {
          ...point,
          iconColor: getColor(
            departureDeadlineTime
              ? point.point.timestamps.completedAt
              : point.point.timestamps.arrivedAt,
            departureDeadlineTime ?? arrivalDeadlineTime,
          ),
        };
      })
      .toReversed();
  });
  protected acceptedProposal = computed(
    () =>
      this._proposals().find(
        ({ status }) => status === OrderProposalStatus.ACCEPTED,
      ) ?? null,
  );
  protected proposals = computed(() =>
    this._proposals()
      .filter(({ status }) => status !== OrderProposalStatus.ACCEPTED)
      .map((proposal) => ({
        ...proposal,
        ...this.getProposalHistory(proposal),
      })),
  );

  private _points = signal<ActivityPoint[]>([]);
  private _proposals = signal<OrderProposal[]>([]);

  constructor() {
    const orderData = this.data.order;
    if (orderData) this.order.set(orderData);

    this.activity$
      .pipe(
        startWith(undefined),
        tap(() => this.loading.set(true)),
        switchMap(() => {
          const request$ = orderData
            ? this.ordersService.getOrderActivity(orderData.uuid)
            : this.delegationsService.getDelegationActivity(
                this.data.delegation!.uuid,
              );

          return request$.pipe(
            catchError(() => {
              this._points.set([]);
              this._proposals.set([]);
              this.loading.set(false);
              this.error.set(true);

              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ points, proposals }) => {
        this._points.set(points);
        this._proposals.set(proposals ?? []);
        this.loading.set(false);
        this.error.set(false);
      });

    const reload$ = orderData
      ? this.ordersService.reload$
      : this.delegationsService.reload$;

    reload$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.activity$.next());
  }

  private getProposalHistory(proposal: OrderProposal): ProposalHistory {
    const { type, rejection, cancelation, creation } = proposal;
    const isEngine = type === OrderProposalType.ENGINE;

    if (rejection) {
      return {
        title: 'labels.engineProposalRejection',
        icon: 'no-entry-filled',
        color: 'red',
        date: rejection.createdAt,
        reason:
          rejection.reason === OrderRejectReason.OTHER
            ? rejection.other
            : `${DictionaryCode.ORDER_REJECT_REASON}.${rejection.reason}`,
        author: rejection.author,
        authorLabel: 'labels.rejectionAuthor',
      };
    }

    if (cancelation) {
      return {
        title: `${this.I18N}.${isEngine ? 'engineProposalCancelation' : 'manualProposalCancelation'}`,
        icon: 'block-filled',
        color: 'gray',
        date: cancelation.createdAt,
        reason: `${DictionaryCode.ORDER_PROPOSAL_CANCELATION_REASON}.${cancelation.reason}`,
        author: null,
        authorLabel: null,
      };
    }

    return {
      title: `${this.I18N}.${isEngine ? 'engineProposal' : 'manualProposal'}`,
      icon: isEngine ? 'decision-filled' : 'manual-filled',
      color: isEngine ? 'blue' : 'violet',
      date: creation.createdAt,
      reason: null,
      author: creation.author,
      authorLabel: null,
    };
  }
}
